import React, { useEffect, useState } from "react";
import {
  Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber,
  ColorPicker, Radio, Divider, Empty,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";

import {
  getActivityTypesApi,
  createActivityTypeApi,
  updateActivityTypeApi,
  deleteActivityTypeApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const ACCENT = "#534ab7";

const ICON_OPTIONS = [
  "nutrition-outline", "moon-outline", "water-outline", "medical-outline",
  "star-outline", "trending-up-outline", "alarm-outline", "footsteps-outline",
  "thermometer-outline", "fitness-outline", "happy-outline", "bed-outline",
  "sparkles-outline", "heart-outline", "book-outline", "grid-outline",
];

const INPUT_TYPE_OPTIONS = [
  { value: "TEXT",     label: "Text" },
  { value: "TEXTAREA", label: "Text area" },
  { value: "NUMBER",   label: "Number" },
  { value: "DURATION", label: "Duration (hr / min)" },
  { value: "SELECT",   label: "Select (choices)" },
];

const DISPLAY_STYLE_OPTIONS = [
  { value: "segment", label: "Segmented buttons — few choices" },
  { value: "chips",    label: "Chip grid — more choices" },
];

// Derives a stable camelCase key from a human label — "Amount (ml)" -> "amountMl".
// Used for both a field's fieldKey and a SELECT option's value, matching the shape
// already stored for the 6 seeded types (see ActivityTypeSeeder.java on the backend).
function toKey(label) {
  const words = (label || "").replace(/[^a-zA-Z0-9\s]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  return words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join("");
}

export default function ActivityTypes() {
  const [types,    setTypes]    = useState([]);
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [form] = Form.useForm();

  const fetchTypes = async () => {
    try {
      const res = await getActivityTypesApi();
      setTypes((res.data.data || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } catch {
      message.error("Failed to load activity types");
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const displayed = types.filter(
    (t) => !search || t.label?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      icon: ICON_OPTIONS[0], color: ACCENT, active: true, quickLogEnabled: false,
      sortOrder: types.length + 1, fields: [],
    });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      typeKey: record.typeKey,
      label:   record.label,
      icon:    record.icon || ICON_OPTIONS[0],
      emoji:   record.emoji,
      color:   record.color || ACCENT,
      sortOrder: record.sortOrder ?? 0,
      active:  record.active,
      quickLogEnabled: record.quickLogEnabled,
      fields: (record.fields || []).map((f) => ({
        ...f,
        options: f.options || [],
      })),
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        typeKey: editing ? editing.typeKey : (values.typeKey || toKey(values.label)),
        label:   values.label,
        icon:    values.icon,
        emoji:   values.emoji,
        color:   typeof values.color === "string" ? values.color : values.color?.toHexString(),
        sortOrder: values.sortOrder ?? 0,
        active:  values.active,
        quickLogEnabled: values.quickLogEnabled,
        fields: (values.fields || []).map((f, i) => ({
          fieldKey:  f.fieldKey || toKey(f.label),
          label:     f.label,
          inputType: f.inputType,
          unit:      f.inputType === "NUMBER" ? f.unit : undefined,
          placeholder: (f.inputType === "TEXT" || f.inputType === "TEXTAREA") ? f.placeholder : undefined,
          required:  !!f.required,
          sortOrder: i,
          displayStyle: f.inputType === "SELECT" ? (f.displayStyle || "segment") : undefined,
          options: f.inputType === "SELECT"
            ? (f.options || []).map((o) => ({ value: o.value || toKey(o.label), label: o.label, emoji: o.emoji || "" }))
            : undefined,
        })),
      };
      if (editing) {
        await updateActivityTypeApi(editing.id, payload);
        message.success("Activity type updated");
      } else {
        await createActivityTypeApi(payload);
        message.success("Activity type added");
      }
      setOpen(false);
      fetchTypes();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save activity type");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateActivityTypeApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Activity type deactivated" : "Activity type activated");
      fetchTypes();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this activity type?",
      content: "Existing logged activities of this type keep their data, but it will no longer be selectable in the app.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteActivityTypeApi(id);
          message.success("Activity type deleted");
          fetchTypes();
        } catch {
          message.error("Failed to delete activity type");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Name",
      dataIndex: "label",
      render: (v, r) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "#111827" }}>
          <span
            style={{
              width: 24, height: 24, borderRadius: 6, flexShrink: 0,
              background: (r.color || ACCENT) + "20",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13,
            }}
          >
            {r.emoji || "●"}
          </span>
          {v}
          <span style={{ color: "#9CA3AF", fontWeight: 400, fontSize: 12 }}>({r.typeKey})</span>
        </span>
      ),
    },
    {
      title: "Fields",
      dataIndex: "fields",
      width: 90,
      align: "center",
      render: (v) => <Tag>{(v || []).length}</Tag>,
    },
    {
      title: "Quick Log",
      dataIndex: "quickLogEnabled",
      width: 100,
      align: "center",
      render: (v) => v ? <Tag color="blue">Yes</Tag> : <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      align: "center",
      render: (v) => v ? <Tag color="green">Active</Tag> : <Tag color="default">Off</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Activity Types</div>
          <div className="page-sub">{types.length} types — the "Log Activity" form and choices are built from these, no app update needed</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Activity Type
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search activity types…"
          allowClear
          style={{ width: 280, borderRadius: 8 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <AppTable columns={columns} data={displayed} />

      <Modal
        title={editing ? `Edit — ${editing.label}` : "Add Activity Type"}
        open={open}
        width={720}
        okText={editing ? "Save Changes" : "Add Activity Type"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 2 }}>
              <AppInput
                name="label"
                label="Name"
                rules={[{ required: true, message: "Name is required" }]}
                placeholder="e.g. Feeding, Sleep, Diaper"
                onChange={(e) => {
                  if (!editing && !form.getFieldValue("typeKey")) {
                    form.setFieldsValue({ typeKey: toKey(e.target.value) });
                  }
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput
                name="typeKey"
                label="Type Key"
                rules={[{ required: true, message: "Required" }]}
                disabled={!!editing}
                placeholder="auto from name"
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="icon"
                label="Icon"
                rules={[{ required: true, message: "Pick an icon" }]}
                options={ICON_OPTIONS.map((i) => ({ value: i, label: i }))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput name="emoji" label="Emoji" placeholder="🍼" />
            </div>
            <Form.Item name="color" label="Color" rules={[{ required: true, message: "Pick a color" }]}>
              <ColorPicker format="hex" />
            </Form.Item>
            <Form.Item name="sortOrder" label="Order">
              <InputNumber min={0} style={{ width: 80 }} />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 24 }}>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="quickLogEnabled" label="Show in Home Quick Log" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>

          <Divider orientation="left" plain>Form Fields</Divider>
          <Form.List name="fields">
            {(fields, { add, remove }) => (
              <>
                {fields.length === 0 && (
                  <Empty description="No fields yet — this type will only collect a title/notes" style={{ margin: "12px 0" }} />
                )}
                {fields.map((field) => (
                  <div key={field.key} style={{ border: "1px solid #EEE", borderRadius: 8, padding: 12, marginBottom: 10, background: "#FAFAFB" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={{ flex: 2 }}>
                        <Form.Item name={[field.name, "label"]} label="Field Label" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 8 }}>
                          <Input
                            placeholder="e.g. Amount, Dose"
                            onChange={(e) => {
                              const cur = form.getFieldValue(["fields", field.name, "fieldKey"]);
                              if (!cur) {
                                form.setFieldValue(["fields", field.name, "fieldKey"], toKey(e.target.value));
                              }
                            }}
                          />
                        </Form.Item>
                      </div>
                      <div style={{ flex: 1.5 }}>
                        <Form.Item name={[field.name, "fieldKey"]} label="Field Key" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 8 }}>
                          <Input placeholder="auto from label" />
                        </Form.Item>
                      </div>
                      <Form.Item label=" " style={{ marginBottom: 8 }}>
                        <Button danger type="text" icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                      </Form.Item>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <div style={{ width: 220 }}>
                        <AppSelect
                          name={[field.name, "inputType"]}
                          label="Input Type"
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 8 }}
                          options={INPUT_TYPE_OPTIONS}
                        />
                      </div>
                      <Form.Item name={[field.name, "required"]} label="Required" valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Switch size="small" />
                      </Form.Item>
                    </div>

                    <Form.Item noStyle shouldUpdate={(prev, cur) =>
                      prev.fields?.[field.name]?.inputType !== cur.fields?.[field.name]?.inputType
                    }>
                      {() => {
                        const inputType = form.getFieldValue(["fields", field.name, "inputType"]);
                        if (inputType === "NUMBER") {
                          return (
                            <Form.Item name={[field.name, "unit"]} label="Unit" style={{ marginBottom: 4, maxWidth: 160 }}>
                              <Input placeholder="e.g. ml, kg, cm" />
                            </Form.Item>
                          );
                        }
                        if (inputType === "TEXT" || inputType === "TEXTAREA") {
                          return (
                            <Form.Item name={[field.name, "placeholder"]} label="Placeholder" style={{ marginBottom: 4 }}>
                              <Input placeholder="e.g. e.g. 400 IU / 5ml" />
                            </Form.Item>
                          );
                        }
                        if (inputType === "SELECT") {
                          return (
                            <>
                              <Form.Item name={[field.name, "displayStyle"]} label="Display As" style={{ marginBottom: 8 }} initialValue="segment">
                                <Radio.Group options={DISPLAY_STYLE_OPTIONS} />
                              </Form.Item>
                              <div style={{ paddingLeft: 4 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Choices</div>
                                <Form.List name={[field.name, "options"]}>
                                  {(opts, { add: addOpt, remove: removeOpt }) => (
                                    <>
                                      {opts.map((opt) => (
                                        <div key={opt.key} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                                          <Form.Item name={[opt.name, "emoji"]} noStyle>
                                            <Input placeholder="🍼" style={{ width: 56 }} />
                                          </Form.Item>
                                          <Form.Item name={[opt.name, "label"]} noStyle rules={[{ required: true, message: "Required" }]}>
                                            <Input
                                              placeholder="Label, e.g. Bottle"
                                              style={{ width: 180 }}
                                              onChange={(e) => {
                                                const cur = form.getFieldValue(["fields", field.name, "options", opt.name, "value"]);
                                                if (!cur) {
                                                  form.setFieldValue(["fields", field.name, "options", opt.name, "value"], toKey(e.target.value));
                                                }
                                              }}
                                            />
                                          </Form.Item>
                                          <Form.Item name={[opt.name, "value"]} noStyle rules={[{ required: true, message: "Required" }]}>
                                            <Input placeholder="value (auto)" style={{ width: 140 }} />
                                          </Form.Item>
                                          <Button danger type="text" size="small" icon={<MinusCircleOutlined />} onClick={() => removeOpt(opt.name)} />
                                        </div>
                                      ))}
                                      <Button size="small" onClick={() => addOpt()}>+ Add choice</Button>
                                    </>
                                  )}
                                </Form.List>
                              </div>
                            </>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                  </div>
                ))}
                <Button block onClick={() => add({ inputType: "TEXT", required: false })} style={{ borderStyle: "dashed" }}>
                  + Add Field
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
